import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ fullName: "", address: "", city: "", zip: "", country: "" });
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);

  if (items.length === 0 && !order) { navigate("/shop"); return null; }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping: form,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setLoading(false); }
  };

  if (order) {
    return (
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-24 text-center" data-testid="checkout-success">
        <CheckCircle weight="fill" size={64} className="text-primary mx-auto mb-6" />
        <h1 className="text-3xl font-modern font-light mb-3">Order confirmed</h1>
        <p className="text-muted-foreground mb-2">Thank you — your Wigglez order is on its way.</p>
        <p className="text-sm text-muted-foreground mb-8">Order total: <span className="text-primary">${order.total.toLocaleString()}</span></p>
        <button onClick={() => navigate("/account")} className="bg-primary text-primary-foreground px-8 py-3.5 rounded-full font-modern font-medium hover:bg-primary/90 transition-colors" data-testid="checkout-view-orders">
          View my orders
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14" data-testid="checkout-page">
      <h1 className="text-4xl tracking-tighter font-light mb-8">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-10">
        <form onSubmit={submit} className="lg:col-span-2 space-y-4" data-testid="checkout-form">
          <p className="overline text-primary mb-1">Shipping details</p>
          <input required placeholder="Full name" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-3 outline-none focus:border-primary transition-colors" data-testid="co-name" />
          <input required placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-3 outline-none focus:border-primary transition-colors" data-testid="co-address" />
          <div className="grid grid-cols-2 gap-4">
            <input required placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="bg-transparent border border-white/20 rounded-lg px-4 py-3 outline-none focus:border-primary transition-colors" data-testid="co-city" />
            <input required placeholder="ZIP / Postcode" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })}
              className="bg-transparent border border-white/20 rounded-lg px-4 py-3 outline-none focus:border-primary transition-colors" data-testid="co-zip" />
          </div>
          <input required placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
            className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-3 outline-none focus:border-primary transition-colors" data-testid="co-country" />
          <div className="p-4 rounded-lg border border-primary/20 bg-primary/5 text-sm text-muted-foreground">
            Secure checkout via Stripe. Test card: <span className="text-white">4242 4242 4242 4242</span>, any future expiry &amp; CVC.
          </div>
          <button disabled={loading} className="w-full bg-primary text-primary-foreground py-3.5 rounded-full font-modern font-medium hover:bg-primary/90 transition-colors disabled:opacity-60" data-testid="co-place-order">
            {loading ? "Redirecting to secure checkout…" : `Pay · $${total.toLocaleString()}`}
          </button>
        </form>

        <div className="h-fit p-6 rounded-xl bg-card border border-white/10">
          <h2 className="font-modern text-lg font-medium mb-4">Summary</h2>
          <div className="space-y-3 mb-4">
            {items.map((it) => (
              <div key={it.product_id} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{it.name} × {it.quantity}</span>
                <span>${(it.price * it.quantity).toLocaleString()}</span>
              </div>
            ))}
          </div>
          <div className="h-px bg-white/10 my-3" />
          <div className="flex justify-between text-lg font-modern"><span>Total</span><span className="text-primary">${total.toLocaleString()}</span></div>
        </div>
      </div>
    </div>
  );
}
